import { Spinner } from "@/components/ui/spinner"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { useEffect } from "react"
import { AribaOnboardingView, type StepId } from "./AribaOnboardingView"
import {
  dismissAribaOnboarding,
  readStepBaseline,
  writeStepBaseline,
} from "./referralSource"
import { useAribaOnboarding } from "./useAribaOnboarding"

interface AribaOnboardingProps {
  onClose: () => void
}

export const AribaOnboarding: React.FC<AribaOnboardingProps> = ({
  onClose,
}) => {
  const {
    isLoading,
    statusSettled,
    browser,
    uploadedFiles,
    extensionInstalled,
    documentsDone,
    extensionDone,
    aribaDone,
    confirmFirefox,
  } = useAribaOnboarding()

  const steps: [StepId, boolean][] = [
    ["documents", documentsDone],
    ["extension", extensionDone],
    ["ariba", aribaDone],
  ]
  const currentStep = steps.find(([, isDone]) => !isDone)?.[0]
  const isFinished = !isLoading && currentStep === undefined

  useEffect(() => {
    trackEvent(AnalyticsEvent.ARIBA_ONBOARDING_VIEWED, { browser })
  }, [browser])

  // Only steps finished after this tab first showed the checklist count (the
  // baseline lives in sessionStorage, see readStepBaseline). It waits for the
  // extension's first answer, else an extension that is already signed in
  // would count as newly done.
  useEffect(() => {
    if (!statusSettled) return
    const done: Record<StepId, boolean> = {
      documents: documentsDone,
      extension: extensionDone,
      ariba: aribaDone,
    }
    const before = readStepBaseline()
    if (before) {
      for (const id of Object.keys(done) as StepId[]) {
        if (done[id] && !before[id]) {
          trackEvent(AnalyticsEvent.ARIBA_ONBOARDING_STEP_COMPLETED, {
            step: id,
            browser,
          })
        }
      }
    }
    writeStepBaseline(done)
  }, [statusSettled, documentsDone, extensionDone, aribaDone, browser])

  // The finished screen shows once; the next visit gets the normal home.
  useEffect(() => {
    if (isFinished) dismissAribaOnboarding()
  }, [isFinished])

  const dismiss = () => {
    trackEvent(AnalyticsEvent.ARIBA_ONBOARDING_DISMISSED, {
      step: currentStep,
      browser,
    })
    dismissAribaOnboarding()
    onClose()
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6 text-grey5" />
      </div>
    )
  }

  return (
    <AribaOnboardingView
      browser={browser}
      uploadedFiles={uploadedFiles}
      extensionInstalled={extensionInstalled}
      documentsDone={documentsDone}
      extensionDone={extensionDone}
      aribaDone={aribaDone}
      currentStep={currentStep}
      onStoreClick={() =>
        trackEvent(AnalyticsEvent.ARIBA_EXTENSION_STORE_CLICKED, { browser })
      }
      onExtensionInstalled={() => {
        if (browser === "firefox") confirmFirefox()
        // Firefox: the extension signs in from this page's session on the
        // next load. Chrome and Edge: a tab opened before the install may not
        // reach the extension until it is loaded again.
        window.location.reload()
      }}
      onDismiss={dismiss}
      onClose={onClose}
    />
  )
}
